import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Search, ChevronDown, Check } from 'lucide-react';

/**
 * Top filter bar for the Items page: search input + Category, Subcategory, Brand
 * popovers + a Clear-filters button. Owns its own popover open/close state.
 */
export const ItemsFilterBar = ({
  searchQuery, setSearchQuery,
  selectedParentCategory, setSelectedParentCategory,
  selectedCategory, setSelectedCategory,
  selectedBrand, setSelectedBrand,
  parentCategories, filterSubcategories, brands,
}) => {
  const [parentOpen, setParentOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  const anyActive =
    selectedParentCategory !== 'all' ||
    selectedCategory !== 'all' ||
    selectedBrand !== 'all' ||
    !!searchQuery;

  return (
    <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={20} />
            <Input
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
              data-testid="search-items-input"
            />
          </div>

          <Popover open={parentOpen} onOpenChange={setParentOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[180px] h-12 justify-between rounded-lg border-gray-300 font-normal" data-testid="parent-category-filter">
                <span className="truncate">{selectedParentCategory !== 'all' ? parentCategories.find(c => c.id === selectedParentCategory)?.category_name || 'All Categories' : 'All Categories'}</span>
                <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search categories..." />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>No results found</CommandEmpty>
                  <CommandGroup>
                    <CommandItem onSelect={() => { setSelectedParentCategory('all'); setParentOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${selectedParentCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                      All Categories
                    </CommandItem>
                    {parentCategories.map(cat => (
                      <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setSelectedParentCategory(cat.id); setParentOpen(false); }}>
                        <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${selectedParentCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                        {cat.category_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Popover open={subOpen} onOpenChange={setSubOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[180px] h-12 justify-between rounded-lg border-gray-300 font-normal" data-testid="subcategory-filter" disabled={selectedParentCategory === 'all'}>
                <span className="truncate">{selectedCategory !== 'all' ? filterSubcategories.find(c => c.id === selectedCategory)?.category_name || 'All Subcategories' : 'All Subcategories'}</span>
                <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search subcategories..." />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>No results found</CommandEmpty>
                  <CommandGroup>
                    <CommandItem onSelect={() => { setSelectedCategory('all'); setSubOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${selectedCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                      All Subcategories
                    </CommandItem>
                    {filterSubcategories.map(cat => (
                      <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setSelectedCategory(cat.id); setSubOpen(false); }}>
                        <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${selectedCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                        {cat.category_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Popover open={brandOpen} onOpenChange={setBrandOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[180px] h-12 justify-between rounded-lg border-gray-300 font-normal" data-testid="brand-filter">
                <span className="truncate">{selectedBrand !== 'all' ? brands.find(b => b.id === selectedBrand)?.brand_name || 'All Brands' : 'All Brands'}</span>
                <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search brands..." />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>No results found</CommandEmpty>
                  <CommandGroup>
                    <CommandItem onSelect={() => { setSelectedBrand('all'); setBrandOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${selectedBrand === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                      All Brands
                    </CommandItem>
                    {brands.map(brand => (
                      <CommandItem key={brand.id} value={brand.brand_name} onSelect={() => { setSelectedBrand(brand.id); setBrandOpen(false); }}>
                        <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${selectedBrand === brand.id ? 'opacity-100' : 'opacity-0'}`} />
                        {brand.brand_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {anyActive && (
            <Button
              type="button"
              variant="ghost"
              className="h-12 text-sm text-[#6B7280] hover:text-red-500 px-3"
              onClick={() => { setSelectedParentCategory('all'); setSelectedCategory('all'); setSelectedBrand('all'); setSearchQuery(''); }}
              data-testid="items-clear-filters-btn"
            >
              Clear filters
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ItemsFilterBar;
